import { Grid } from "@material-ui/core";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import WidgetTitle from "features/landing/components/FleetManagementDashboard/WidgetTitle/WidgetTitle";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import regression from "regression";
import { theme } from "styles/theme";
import { dateFilterTypes } from "../../../../loads/components/LoadsDashboard/loadsDashboardConsts";
import { lotColors } from "../NewWashDashboardConsts";
import { NewWashDashboardStyle } from "../NewWashDashboardStyle";

const AssetsWashedByLot = ({ washes, isLoading }) => {
    const classes = NewWashDashboardStyle();

    const { activityFilters } = useSelector(state => state.washDashboardState);

    const [hoveredLotId, setHoveredLotId] = useState(null);

    const isByDay = activityFilters['dateFilterType'].value < dateFilterTypes.QUARTER;
    const isByMonth = activityFilters['dateFilterType'].value == dateFilterTypes.MONTH;
    const key = isByDay ? 'day' : 'month';
    const format = isByDay ? "YYYY-MM-DD" : "YYYY-MM";

    const groupedDataByLot = useMemo(() => {
        if (!washes || washes.length === 0) return {};

        const lotsGroup = {};
        washes.forEach(wash => {
            const lotId = wash.lotId;
            if (!lotsGroup[lotId]) {
                lotsGroup[lotId] = {
                    lotName: wash.lotName,
                    data: {},
                };
            }
            let groupKey = wash.groupKey;
            let name;
            if (isByMonth) {
                const week = moment(groupKey).isoWeek();
                const year = moment(groupKey).isoWeekYear();
                groupKey = `${year}-W${week}`;
                name = `week ${week}`;
            } else if (isByDay) {
                name = moment(groupKey).format("DD/MM");
            } else {
                name = moment(groupKey).format("MMM");
            }
            if (!lotsGroup[lotId].data[groupKey]) {
                lotsGroup[lotId].data[groupKey] = {
                    groupKey,
                    count: 0,
                    name,
                    month: wash.month,
                    day: wash.day,
                };
            }
            lotsGroup[lotId].data[groupKey].count += 1;
        });

        const start = moment(activityFilters['startDate'].value).startOf(key);
        const end = moment(activityFilters['endDate']?.value).startOf(key);
        let keys = [];
        let regressionKeys = new Set();
        if (isByMonth) {
            let current = start.clone();
            let seen = new Set();
            while (current <= end) {
                const week = current.isoWeek();
                const year = current.isoWeekYear();
                const weekKey = `${year}-W${week}`;
                if (!seen.has(weekKey)) {
                    keys.push(weekKey);
                    seen.add(weekKey);
                    regressionKeys.add(weekKey);
                }
                current = current.add(1, 'week');
            }
        } else {
            let currentKey = start.clone();
            while (currentKey <= end) {
                const currentKeyStr = currentKey.format(format);
                keys.push(currentKeyStr);
                currentKey = currentKey.add(1, key);
                //Only calculate regression if this key is in the past
                if (currentKey <= moment().startOf('day')) {
                    regressionKeys.add(currentKeyStr);
                }
            }
        }

        Object.values(lotsGroup).forEach(lot => {
            keys.forEach(k => {
                let name;
                if (isByMonth) {
                    const week = parseInt(k.split('-W')[1], 10);
                    name = `week ${week}`;
                } else if (isByDay) {
                    name = moment(k).format("DD/MM");
                } else {
                    name = moment(k).format("MMM");
                }
                if (!lot.data[k]) {
                    lot.data[k] = {
                        groupKey: k,
                        count: 0,
                        name,
                        month: k,
                        day: k,
                    };
                } else {
                    lot.data[k].name = name;
                }
            });
            lot.data = keys.map(k => lot.data[k]);
        });

        // Calculate total counts for each time point
        const totalsByTimePoint = keys?.filter(k => regressionKeys.has(k)).map((k, index) => {
            const total = Object.values(lotsGroup).reduce((sum, lot) => {
                return sum + (lot.data?.find(d => d.groupKey == k)?.count || 0);
            }, 0);
            return [index, total];
        });

        // Calculate overall regression
        const result = regression.linear(totalsByTimePoint);
        const overallRegression = {
            slope: result.equation[0],
            intercept: result.equation[1],
            r2: result.r2,
            predict: (x) => result.equation[0] * x + result.equation[1]
        };

        // Add trend line points to each data point
        Object.values(lotsGroup).forEach(lot => {
            lot.regression = overallRegression;
            lot.data = lot.data.map((point, index) => ({
                ...point,
                trend: overallRegression.predict(index) / Object.keys(lotsGroup).length,
                lotData: lot
            }));
        });

        return lotsGroup;
    }, [washes, activityFilters, key, format, isByDay, isByMonth]);

    const CustomTooltip = ({ label, payload }) => {
        if (!payload || payload.length === 0 || isLoading) return null;

        const washCounts = payload.filter(item => item.dataKey === 'count');
        const trendInfo = washCounts[0]?.payload?.lotData?.regression;

        return (
            <div className={classes.graphTooltipCustom}>
                <div className={classes.graphTooltipLabel}>{label}</div>
                {trendInfo && (
                    <div className={classes.graphTooltipTrendWrapper}>
                        <div className={classes.graphTooltipItem}>
                            {`Trend: ${washCounts[0]?.payload?.trend.toFixed(1)}, Slope: ${trendInfo.slope > 0 ? '+' : ''}${trendInfo.slope.toFixed(2)}, R² = ${(trendInfo.r2 * 100).toFixed(1)}%`}
                        </div>
                    </div>
                )}
                {washCounts.length === 0 ?
                    <div className={classes.graphTooltipNoWashes}>
                        No washes
                    </div> :
                    <div className={`${classes.graphTooltipItemsWrapper}`} style={{
                        gridTemplateColumns: `repeat(${Math.min(3, washCounts.length)}, auto)`
                    }}>
                        {washCounts.map((item, idx) => (
                            <div key={idx} className={`${classes.graphTooltipItem} ${classes.graphTooltipItemGrid}`} style={{ color: item.color }}>
                                <span className={classes.graphTooltipItemName}>
                                    {item.payload.lotName || item.name}
                                </span>
                                <span className={classes.graphTooltipItemValue}>
                                    {item.value}
                                </span>
                            </div>
                        ))}
                    </div>
                }
            </div>
        );
    };

    const mapExportData = (a => {
        return {
            ['Lot']: a.lotName,
            ['Time Period']: a.groupKey,
            ['Total']: a.count,
        };
    });

    const dataToExport = Object.values(groupedDataByLot).flatMap(lot =>
        lot.data.map(d => ({
            ...d,
            lotName: lot.lotName,
        }))
    );

    return <Grid container direction="column">
        <Grid container justifyContent='space-between' alignItems="center">
            <WidgetTitle
                title='Assets Washed By Lot'
                loading={isLoading}
                mapExportData={mapExportData}
                dataToExport={dataToExport}
            />
        </Grid>

        <Grid item>
            {!!Object.values(groupedDataByLot)?.length ? <ResponsiveContainer width="100%" height={300}>
                <LineChart
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                >
                    <XAxis
                        axisLine={{ stroke: '#ccc' }}
                        tickLine={false}
                        tick={{ fontSize: 10 }}
                        dataKey="name"
                        interval={0}
                        type="category"
                        allowDuplicatedCategory={false}
                    />
                    <YAxis
                        axisLine={{ stroke: '#ccc' }}
                        width={30}
                        tickLine={false}
                        tick={{ color: theme.palette.primary.main, fontSize: '0.7em', fontWeight: 'bold' }}
                        domain={[0, 'auto']}
                        tickCount={6}
                        allowDecimals={false}
                    />
                    <Tooltip
                        content={<CustomTooltip />}
                        wrapperStyle={{ zIndex: 1000 }}
                    />

                    {Object.keys(groupedDataByLot).length <= 20 &&
                        <Legend
                            iconType="circle"
                            layout="horizontal"
                            verticalAlign="bottom"
                            iconSize={10}
                            formatter={(value, entry, index) =>
                                <span className={classes.legendLabel}>{value}</span>
                            }
                            payload={
                                Object.entries(groupedDataByLot).map(([lotId, lot], idx) => ({
                                    value: lot.lotName || lotId,
                                    type: "circle",
                                    color: lotColors[idx % lotColors.length],
                                    id: lotId,
                                }))}
                        />}

                    {Object.entries(groupedDataByLot).map(([lotId, lot], idx) => (
                        <>
                            <Line
                                key={`${lotId}-data`}
                                data={lot.data}
                                dataKey="count"
                                name={lot.lotName}
                                stroke={lotColors[idx % lotColors.length]}
                                dot={false}
                                isAnimationActive={false}
                                type="monotone"
                                strokeWidth={hoveredLotId === lotId ? 2 : 1}
                                opacity={hoveredLotId && hoveredLotId !== lotId ? 0.3 : 1}
                                onMouseOver={() => setHoveredLotId(lotId)}
                                onMouseOut={() => setHoveredLotId(null)}
                            />
                            <Line
                                key={`${lotId}-trend`}
                                data={lot.data}
                                dataKey="trend"
                                name={`${lot.lotName} Trend`}
                                stroke={lotColors[idx % lotColors.length]}
                                strokeDasharray="5 5"
                                dot={false}
                                isAnimationActive={false}
                                legendType="none"
                                strokeWidth={hoveredLotId === lotId ? 2 : 1}
                                opacity={hoveredLotId && hoveredLotId !== lotId ? 0.3 : 1}
                            />
                        </>
                    ))}
                </LineChart>
            </ResponsiveContainer>
                : <EmptyPlaceholder text='No Assets Washed' />}
        </Grid>
    </Grid>
}
export default AssetsWashedByLot;